"use client";
import { useMapContext } from "@components/sections/mapSection/MapProvider";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from "@components/ui/dialog";
import { getUniqueQueryKeys } from "@lib/utils/getUniqueQueryKeys";
import { useRoutes } from "@lib/utils/hooks/useRoutes";
import { MapFilters } from "@lib/utils/types/filters";
import { Accordion } from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { useMapForm } from "./mapForm.hooks";
import { MapFormFieldMapper } from "./MapFormFieldMapper";

interface IMapFormDialog {
  filters: MapFilters;
  label?: string;
  facilitiesLength?: number;
}

export function MapFormDialog({
  filters,
  facilitiesLength = 0,
}: IMapFormDialog) {
  const [query, onSelect, toggleAll] = useMapForm({
    allowedQueryKeys: getUniqueQueryKeys(filters),
  });
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations("Filters");
  const routes = useRoutes();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [returnUrl, setReturnUrl] = useState("");
  const { clearMarkerPopup } = useMapContext();

  return (
    <Dialog
      open={isDialogOpen}
      onOpenChange={state => {
        setIsDialogOpen(state);
        state
          ? setReturnUrl(`${pathname}?${searchParams.toString()}`)
          : router.push(returnUrl);
      }}
    >
      <DialogTrigger
        onClick={() => setIsDialogOpen(true)}
        className="absolute bottom-[75px] left-1/2 z-50 flex -translate-x-1/2 -translate-y-1/2 transform items-center gap-3 rounded-xl bg-white px-4  py-3 text-primary shadow-lg md:hidden"
      >
        {t("Title")}
        <ChevronDown className="h-4 w-4 shrink-0" />
      </DialogTrigger>
      <DialogContent className="flex h-full min-w-full flex-col">
        <form className="h-full flex-1 overflow-y-scroll scrollbar-none">
          <span className="my-4 block text-2xl font-bold"> {t("Title")}</span>
          <Accordion type="multiple" className="w-full">
            {filters.entries.map(filterGroup => (
              <MapFormFieldMapper
                key={filterGroup.id}
                filter={filterGroup}
                onSelect={onSelect}
                toggleAll={toggleAll}
                query={query}
              />
            ))}
          </Accordion>
        </form>
        <DialogFooter className="flex h-20 w-full flex-row items-center justify-between px-6 pl-0">
          <Button
            variant="link"
            onClick={() => {
              clearMarkerPopup();
              router.push(routes.webMap.route());
              setIsDialogOpen(false);
            }}
            className="p-0 text-black"
          >
            {t("Actions.Reset")}
          </Button>
          <Button
            onClick={() => {
              setIsDialogOpen(false);
            }}
          >
            {t("Actions.Visit")} {facilitiesLength} {t("Actions.Results")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
